/**
 * Edit a saved mapping profile. SPEC-0001 step 6.
 *
 * Profiles are side-agnostic, so the fields are grouped by what they are for
 * rather than by which file they came from: the three columns shared by both
 * exports, then the ones only a sold file has, then the ones only a PSA budget
 * has. A profile may legitimately fill in any subset.
 *
 * Column names are free text here, because there is no file in context to
 * validate against — validation happens when the profile is applied to an
 * actual upload, where a saved header that the file lacks is simply ignored.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fieldsFor, type CanonicalField, type MappingProfile } from '@picklii/shared';
import type { RoleAlias } from '@picklii/recon';
import { AppShell } from '../components/AppShell';
import { ConfirmAction } from '../components/ConfirmAction';
import { api } from '../lib/api';

/**
 * A profile is scoped to one source, so the editor shows exactly the fields
 * that source asks for — nothing from the other side, which it could never
 * fill.
 */
function fieldsForProfile(profile: MappingProfile): CanonicalField[] {
	return [...fieldsFor(profile.sourceType)];
}

export function ProfileDetail() {
	const { id = '' } = useParams();
	const navigate = useNavigate();

	const [profile, setProfile] = useState<MappingProfile | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [saved, setSaved] = useState(false);

	useEffect(() => {
		api.getProfile(id).then(setProfile, (cause: Error) => setError(cause.message));
	}, [id]);

	function patch(changes: Partial<MappingProfile>) {
		setProfile((current) => (current === null ? current : { ...current, ...changes }));
		setSaved(false);
	}

	function setColumn(key: string, value: string) {
		if (profile === null) return;
		const columns = { ...profile.columns };
		if (value.trim() === '') delete columns[key];
		else columns[key] = value;
		patch({ columns });
	}

	async function save() {
		if (profile === null) return;
		setBusy(true);
		setError(null);
		try {
			const next = await api.updateProfile(id, profile);
			setSaved(true);
			// Renaming moves the profile to a new id, so follow it rather than
			// leaving the page pointing at something that no longer exists.
			if (next.id !== undefined && next.id !== id) navigate(`/profiles/${next.id}`, { replace: true });
			else setProfile(next);
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Could not save.');
		} finally {
			setBusy(false);
		}
	}

	async function remove() {
		setBusy(true);
		try {
			await api.deleteProfile(id);
			navigate('/profiles');
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Could not delete.');
			setBusy(false);
		}
	}

	const aliases: RoleAlias[] = profile?.aliases ?? [];

	return (
		<AppShell title={profile?.name ?? 'Mapping profile'}>
			{error !== null && (
				<div className="alert alert-error alert-soft col-span-12" role="alert">
					<span>{error}</span>
				</div>
			)}

			{profile === null && error === null && (
				<div className="col-span-12 flex justify-center py-16">
					<span className="loading loading-spinner" />
				</div>
			)}

			{profile !== null && (
				<>
					<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
						<div className="card-body gap-4">
							<label className="flex flex-col gap-1.5">
								<span className="text-sm font-medium">Name</span>
								<input
									className="input input-bordered input-sm mt-1 max-w-md"
									value={profile.name}
									onChange={(e) => patch({ name: e.target.value })}
								/>
								<span className="text-base-content/70 text-xs">
									The name identifies the profile. Renaming it to one that already exists
									will overwrite that profile.
								</span>
							</label>
						</div>
					</section>

					<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
						<div className="card-body gap-3">
							<div>
								<h2 className="card-title text-base">
									Columns for {profile.sourceType === 'sold' ? 'sold hours' : 'PSA budget'}
								</h2>
								<p className="text-base-content/70 text-sm">
									Free text here, because there is no file in context to check against.
									A saved column the uploaded file lacks is simply ignored when applied.
								</p>
							</div>
							<div className="overflow-x-auto">
								<table className="table table-sm">
									<thead>
										<tr>
											<th>Picklii field</th>
											<th>Your column</th>
										</tr>
									</thead>
									<tbody>
										{fieldsForProfile(profile).map((field) => (
											<tr key={field.key}>
												<td>
													<span className="font-medium">{field.label}</span>
													<span className="text-base-content/70 block font-mono text-xs">
														{field.key}
													</span>
												</td>
												<td>
													<input
														className="input input-bordered input-sm w-full max-w-xs"
														placeholder="not mapped"
														value={profile.columns[field.key] ?? ''}
														onChange={(e) => setColumn(field.key, e.target.value)}
														aria-label={`Column for ${field.label}`}
													/>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</section>

					<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
						<div className="card-body gap-3">
							<div>
								<h2 className="card-title text-base">Role aliases</h2>
								<p className="text-base-content/70 text-sm">
									Declared once, applied on every run. Picklii never infers these — a
									silently wrong join is the failure this product exists to catch.
								</p>
							</div>
							{aliases.map((alias, index) => (
								<div key={index} className="join w-full max-w-2xl">
									<input
										className="input input-bordered input-sm join-item w-full"
										placeholder="Sr. Consultant"
										value={alias.from}
										onChange={(e) =>
											patch({
												aliases: aliases.map((a, i) =>
													i === index ? { ...a, from: e.target.value } : a,
												),
											})
										}
									/>
									<span className="btn btn-sm btn-disabled join-item">means</span>
									<input
										className="input input-bordered input-sm join-item w-full"
										placeholder="Senior Consultant"
										value={alias.to}
										onChange={(e) =>
											patch({
												aliases: aliases.map((a, i) =>
													i === index ? { ...a, to: e.target.value } : a,
												),
											})
										}
									/>
									<button
										className="btn btn-sm btn-outline join-item"
										onClick={() => patch({ aliases: aliases.filter((_, i) => i !== index) })}>
										Remove
									</button>
								</div>
							))}
							<button
								className="btn btn-sm btn-outline self-start"
								onClick={() => patch({ aliases: [...aliases, { from: '', to: '' }] })}>
								Add an alias
							</button>
						</div>
					</section>

					{/* Destructive on the left, the action you actually came for on the
					    right. Save is the primary action, so it sits where the eye
					    finishes rather than where it starts. */}
					<section className="col-span-12 flex flex-wrap items-center justify-end gap-3">
						<span className="me-auto">
							<ConfirmAction
								label="Delete profile"
								confirmLabel={`Delete the mapping “${profile.name}”?`}
								description="Runs that used it keep their own record of the columns they read; only the reusable mapping goes."
								busy={busy}
								onConfirm={() => void remove()}
							/>
						</span>
						{saved && <span className="badge badge-success badge-sm">Saved</span>}
						<button
							className="btn btn-primary btn-sm"
							disabled={busy || profile.name.trim() === ''}
							onClick={() => void save()}>
							{busy ? <span className="loading loading-spinner loading-xs" /> : 'Save changes'}
						</button>
					</section>
				</>
			)}
		</AppShell>
	);
}
